/* @flow */
import _ from 'lodash';
import Glyphicons from '@remind/r101-glyphs';
import getPropClasses from 'lib/getPropClasses';
import * as React from 'react';
import cx from 'classnames';
import GlobalAttributes from 'constants/GlobalAttributes';
import SupportedEventNames from 'constants/SupportedEventNames';
import type { GlyphName } from '@remind/r101-glyphs';

import './styles/Glyph.scss';

const ALLOWED_PROPS = _.union(GlobalAttributes, SupportedEventNames);

export type GlyphSize =
  | 'inherit'
  | 'xxxsmall'
  | 'xxsmall'
  | 'xsmall'
  | 'small'
  | 'medium'
  | 'large'
  | 'xlarge'
  | 'xxlarge'
  | 'xxxlarge';

export type GlyphColor =
  | 'inherit'
  | 'lava'
  | 'flame'
  | 'sunrise'
  | 'shamrock'
  | 'jungle'
  | 'brand'
  | 'violet'
  | 'thunder'
  | 'midnight'
  | 'eclipse'
  | 'koala'
  | 'pitch'
  | 'wolf'
  | 'polar-bear'
  | 'white'
  | 'facebook'
  | 'pinterest'
  | 'caribbean'
  | 'twitter';

type Props = {
  name: GlyphName,
  solo?: ?boolean,
  className?: ?string,
  // remaining used by getPropClasses
  /* eslint-disable react/no-unused-prop-types */
  size?: GlyphSize,
  color?: GlyphColor,
};

/**
 * Glyph provides icons through a web font maintained in the r101-glyphs respository.
 *
 * Glyphicons use a web font and can be styled like text on a web page. You can customize the size
 * and color by simply inheriting the font size and text color of the container the `Glyph` is used
 * in or by specifying a size and/or color through props.
 *
 * _TODO: Use JavaScript to render this example._
 *
 * ```example
 * <div>
 *   <Glyph name="facebook" color="facebook" size="xxxlarge" />
 *   <Glyph name="twitter" color="twitter" size="xxxlarge" />
 *   <Glyph name="youtube" color="lava" size="xxxlarge" />
 * </div>
 * ```
 */

class Glyph extends React.Component<Props> {
  static propClasses = {
    size: {
      xxxsmall: 'Glyph-sizeXxxSmall',
      xxsmall: 'Glyph-sizeXxSmall',
      xsmall: 'Glyph-sizeXSmall',
      small: 'Glyph-sizeSmall',
      medium: 'Glyph-sizeMedium',
      large: 'Glyph-sizeLarge',
      xlarge: 'Glyph-sizeXLarge',
      xxlarge: 'Glyph-sizeXxLarge',
      xxxlarge: 'Glyph-sizeXxxLarge',
    },
    color: {
      lava: 'Glyph--color-lava',
      flame: 'Glyph--color-flame',
      sunrise: 'Glyph--color-sunrise',
      shamrock: 'Glyph--color-shamrock',
      jungle: 'Glyph--color-jungle',
      brand: 'Glyph--color-brand',
      violet: 'Glyph--color-violet',
      thunder: 'Glyph--color-thunder',
      midnight: 'Glyph--color-midnight',
      eclipse: 'Glyph--color-eclipse',
      koala: 'Glyph--color-koala',
      pitch: 'Glyph--color-pitch',
      'polar-bear': 'Glyph--color-polar-bear',
      white: 'Glyph--color-white',
      facebook: 'Glyph--color-facebook',
      pinterest: 'Glyph--color-pinterest',
      twitter: 'Glyph--color-twitter',
      caribbean: 'Glyph--color-caribbean',
    },
  };

  static defaultProps = {
    size: 'inherit',
    color: 'inherit',
  };

  render() {
    const { className, name } = this.props;
    const iconProps = _.pick(this.props, ALLOWED_PROPS);

    return (
      <i
        {...iconProps}
        className={cx(
          'Glyph',
          // $FlowFixMe
          Glyphicons[name] || 'Glyph-unknown',
          { 'Glyph--solo': this.props.solo },
          getPropClasses(this.props, Glyph.propClasses),
          className
        )}
      />
    );
  }
}

export default Glyph;
